import { useMemo, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useTranslation } from "react-i18next";

const HYG_GROUPS = {
  privileges: ["h_domain_admins_limited","h_admin_separate_accounts","h_service_no_privileges","h_delegations_reviewed","h_nested_groups","h_shadow_admins","h_service_interactive"],
  accounts:   ["h_dormant_accounts","h_offboarding","h_service_owner","h_technical_passwords_rotated","h_legacy_protocols"],
  gpo_adcs:   ["h_gpo_documented","h_gpo_monitored","h_adcs_audited"],
  entra:      ["h_mfa_entra","h_hybrid_risks"],
  governance: ["h_remediation_tracked"],
};

function MaturityRow({ label, value, onChange, testId }) {
  const { t } = useTranslation();
  const opts = [
    { id: "yes", label: t("wizard.maturity.yes") },
    { id: "partial", label: t("wizard.maturity.partial") },
    { id: "no", label: t("wizard.maturity.no") },
    { id: "unknown", label: t("wizard.maturity.unknown") },
  ];
  return (
    <div className="py-3 border-b border-slate-100 last:border-0">
      <div className="text-slate-800 leading-relaxed text-[15px]">{label}</div>
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {opts.map((o) => (
          <button key={o.id} type="button" onClick={() => onChange(o.id)}
            data-testid={`${testId}-${o.id}`}
            className={`rounded-sm border px-3 py-2 text-sm transition-colors ${
              value === o.id ? "border-brand-fuchsia bg-fuchsia-50/40 text-brand-bordeaux font-semibold" : "border-slate-200 text-slate-700 hover:border-brand-fuchsia"
            }`}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Step4Hygiene({ ref_data, value, onChange }) {
  const { t } = useTranslation();
  const set = (id, v) => onChange({ answers: { ...(value.answers || {}), [id]: v } });

  const grouped = useMemo(() => {
    const byId = Object.fromEntries((ref_data.hygiene_questions || []).map((q) => [q.id, q]));
    return Object.entries(HYG_GROUPS).map(([gid, ids]) => ({
      id: gid,
      questions: ids.map((id) => byId[id]).filter(Boolean),
    })).filter(g => g.questions.length > 0);
  }, [ref_data.hygiene_questions]);

  // Open first two groups by default (privileges + accounts)
  const [openItems, setOpenItems] = useState(["privileges", "accounts"]);

  return (
    <div className="py-8 space-y-6" data-testid="step-4">
      <p className="text-slate-600 leading-relaxed max-w-3xl text-[15px]">{t("wizard.s4_intro")}</p>

      <Accordion type="multiple" value={openItems} onValueChange={setOpenItems} className="card-flat divide-y divide-slate-100">
        {grouped.map((g) => {
          const done = g.questions.filter((q) => !!value.answers?.[q.id]).length;
          return (
            <AccordionItem key={g.id} value={g.id} className="px-6 border-none">
              <AccordionTrigger className="hover:no-underline py-4" data-testid={`hyg-group-${g.id}`}>
                <div className="flex-1 text-left flex items-center justify-between pr-3">
                  <span className="text-slate-900 font-semibold text-[15px]">{t(`wizard.hyg_groups.${g.id}`)}</span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${done === g.questions.length ? "border-brand-fuchsia bg-fuchsia-50 text-brand-bordeaux" : "border-slate-200 bg-white text-slate-500"}`}>
                    {t("wizard.group_progress", { done, total: g.questions.length })}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="divide-y divide-slate-100">
                  {g.questions.map((q) => (
                    <MaturityRow key={q.id} label={q.label} value={value.answers?.[q.id]} onChange={(v) => set(q.id, v)} testId={`hyg-${q.id}`} />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}

export const step4Valid = (v, questions) => {
  const answered = Object.keys(v.answers || {}).filter((k) => !!v.answers[k]).length;
  return answered >= Math.ceil(questions.length * 0.5);
};
